import * as React from "react";
import {
  Button,
  Image,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
} from "@fluentui/react-components";
import { Attach24Regular } from "@fluentui/react-icons";
import { useFilesQuery } from "../hooks/useFileQueries";
import { downloadFile } from "../services/fileApi";
import { Tooltip } from "@fluentui/react-components";

const MAX_LABEL_LENGTH = 24;

const Files = React.forwardRef(({ entity_type, entity_id, editable = false, ...props }, ref) => {
  const fileInputRef = React.useRef(null);
  const [toBeAdded, setToBeAdded] = React.useState([]);
  const [toBeDeleted, setToBeDeleted] = React.useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = React.useState(null);
  const [iconErrors, setIconErrors] = React.useState(new Set());
  const [loadingIds, setLoadingIds] = React.useState(new Set());

  const { data: attachments = [] } = useFilesQuery(entity_id ? { entity_type, entity_id } : undefined, !!entity_id);

  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    // Get pending attachments to upload
    getPendingAttachments: () => toBeAdded,
    // Get attachments to delete
    getPendingDeletions: () => toBeDeleted,
    // Clear pending attachments after sending
    clearPending: () => {
      setToBeAdded([]);
      setToBeDeleted([]);
    },
  }));

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Add files to toBeAdded state instead of uploading
    setToBeAdded((prev) => [...prev, ...files]);

    // Reset input
    event.target.value = "";
  };

  const handleDelete = (attachment, isNew = false) => {
    if (isNew) {
      // For new files to be added, remove from toBeAdded array
      const index = toBeAdded.indexOf(attachment);
      if (index > -1) {
        setToBeAdded((prev) => prev.filter((_, i) => i !== index));
      }
    } else {
      // For existing attachments, add to toBeDeleted state
      setAttachmentToDelete(attachment);
      setIsDeleteDialogOpen(true);
    }
  };

  const confirmDelete = () => {
    if (attachmentToDelete) {
      setToBeDeleted((prev) => [...prev, attachmentToDelete.id]);
      setAttachmentToDelete(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDownload = async (attachment) => {
    console.log("Downloading attachment:", attachment);
    setLoadingIds((prev) => new Set(prev).add(attachment.public_id));

    try {
      const downloadId = attachment.file_path || attachment.public_id;
      const displayLabel =
        attachment.original_name ||
        attachment.file_name ||
        attachment.filename ||
        attachment.name ||
        attachment.file_path ||
        `#${attachment.public_id}`;

      await downloadFile(downloadId, displayLabel, true);
    } catch (error) {
      console.error("Download failed:", error?.message || error);
    } finally {
      setLoadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.public_id);
        return newSet;
      });
    }
  };

  const getDisplayLabel = (attachment) => {
    return (
      attachment.original_name ||
      attachment.file_name ||
      attachment.filename ||
      attachment.name ||
      attachment.file_path ||
      `#${attachment.public_id}`
    );
  };

  const getExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const renderAttachmentTag = (attachment, isNew = false) => {
    const displayLabel = getDisplayLabel(attachment);
    const extension = getExtension(isNew ? attachment.name : attachment.filename || attachment.name);
    const iconSrc = `/icons/file_type_${extension}.svg`;
    const hasIconError = iconErrors.has(isNew ? `new-${displayLabel}` : attachment.public_id);
    const isDownloading = !isNew && loadingIds.has(attachment.public_id);

    const handleIconError = () => {
      setIconErrors((prev) => new Set(prev).add(isNew ? `new-${displayLabel}` : attachment.public_id));
    };

    // Maintain file extension if label is wrapped
    let labelToShow;
    if (displayLabel.length > MAX_LABEL_LENGTH) {
      // Try to preserve extension
      const ext = extension ? `.${extension}` : "";
      const baseLabel = displayLabel.endsWith(ext)
        ? displayLabel.slice(0, MAX_LABEL_LENGTH - ext.length) + "..." + ext
        : displayLabel.slice(0, MAX_LABEL_LENGTH) + "...";
      labelToShow = baseLabel;
    } else {
      labelToShow = displayLabel;
    }

    return (
      <InteractionTag
        {...props}
        key={isNew ? `new-${displayLabel}` : attachment.public_id}
        shape="rounded"
        size="medium"
      >
        <InteractionTagPrimary
          icon={<Image src={hasIconError ? `/icons/default_file.svg` : iconSrc} onError={handleIconError} />}
          onClick={() => !isNew && !isDownloading && handleDownload(attachment)}
          disabled={isDownloading}
          style={{ cursor: !isNew ? (isDownloading ? "wait" : "pointer") : "default" }}
          hasSecondaryAction={editable}
        >
          {isDownloading ? (
            `${labelToShow}...`
          ) : displayLabel.length > MAX_LABEL_LENGTH ? (
            <Tooltip content={displayLabel} relationship="label">
              <span style={{ wordBreak: "break-all", maxWidth: 180, display: "inline-block" }}>{labelToShow}</span>
            </Tooltip>
          ) : (
            <span style={{ wordBreak: "break-all", maxWidth: 180, display: "inline-block" }}>{labelToShow}</span>
          )}
        </InteractionTagPrimary>
        {editable && <InteractionTagSecondary aria-label="remove" onClick={() => handleDelete(attachment, isNew)} />}
      </InteractionTag>
    );
  };

  if (!editable && attachments.length === 0 && toBeAdded.length === 0) {
    return null;
  }

  return (
    <div style={{ margin: "5px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
      {editable && (
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFileChange} />
      )}
      {editable && <Button icon={<Attach24Regular />} onClick={handleAttachClick} />}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {/* Render existing attachments */}
        {attachments
          ?.filter((att) => !toBeDeleted.includes(att.id))
          .map((attachment) => renderAttachmentTag(attachment, false))}

        {/* Render new files to be added */}
        {toBeAdded?.map((file) => renderAttachmentTag(file, true))}
      </div>

      {attachmentToDelete && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            padding: "24px",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
            zIndex: 1000,
            minWidth: "300px",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <h3>Delete File</h3>
            <p>
              Are you sure you want to delete <strong>{getDisplayLabel(attachmentToDelete)}</strong>?
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setAttachmentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button appearance="primary" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </div>
      )}
      {isDeleteDialogOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 999,
          }}
          onClick={() => {
            setIsDeleteDialogOpen(false);
            setAttachmentToDelete(null);
          }}
        />
      )}
    </div>
  );
});

Files.displayName = "Files";

export default Files;
